<template>
  <component
    :is="resolvedElementVueComponent"
    v-if="elementType && !isLayoutElement"
    ref="renderedElement"
    v-animate="{animationEffects: effectiveAnimationEffects}"
    :identifier="String(elementId)"
    :option-values="entry.options"
    :theme-mode="paintedThemeMode"
    :active-state="activeState"
    :edit-mode="false"
    :class="[elementClasses, {[keepsItsHeightClass]: keepsItsHeight}]"
    v-bind="stagingEditAttrs"
    @pointerenter.stop="triggerAnimation"
    @pointerleave.stop="resetAnimation"
  />
  <component
    :is="resolvedElementVueComponent"
    v-else-if="elementType"
    ref="renderedElement"
    v-animate="{animationEffects: effectiveAnimationEffects}"
    :identifier="String(elementId)"
    :option-values="entry.options"
    :theme-mode="paintedThemeMode"
    :active-state="activeState"
    :edit-mode="false"
    :class="[elementClasses, {[keepsItsHeightClass]: keepsItsHeight}]"
    v-bind="stagingEditAttrs"
    @pointerenter.stop="triggerAnimation"
    @pointerleave.stop="resetAnimation"
  >
    <DynamicComponentProductionElement
      v-for="childId in childrenIds"
      :key="childId"
      :dynamic-component-id="dynamicComponentId"
      :element-id="childId"
      :ancestor-ids="childAncestorIds"
      :in-page-root="holdsPageSections"
      :theme-mode="paintedThemeMode"
      :is-production="isProduction"
    />
  </component>
</template>

<script>
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import { isVisitorThemeMode, readVisitorThemeMode }
  from "%/dynamic-components/render-engine/visitor-theme-mode";
import App from "@bloglovin/inspire-engine/App";
import { PLATFORM_ENTITY_APP_NAME, isBuilderAppBooted, isCompiledRenderSurface }
  from "%/dynamic-component-editor/classes/platform-standard-entities";
import DynamicElement from "%/dynamic-component-editor/classes/dynamic-element";
import DynamicOption from "%/dynamic-component-editor/classes/dynamic-option";
import DynamicComponentError from "%/dynamic-component-editor/errors/dynamic-component-error";
import DomStateMixinProduction
  from "%/dynamic-component-editor/mixins/dom-state-mixin-production";
import AnimationsMixinProduction
  from "%/dynamic-component-editor/mixins/animations-mixin-production";
import AppearanceMixinProduction
  from "%/dynamic-components/render-engine/mixins/production/appearance-mixin-production";
import PositionMixinProduction
  from "%/dynamic-components/render-engine/mixins/production/position-mixin-production";
import { DynamicElementEventMixinProduction }
  from "%/dynamic-component-editor/mixins/dynamic-element-event-mixin-production";
import InvalidElement from "%/dynamic-component-editor/standard-elements/InvalidElement.vue";
import { isStagingRenderSurface, stagingEditAttributes }
  from "%/dynamic-component-editor/helpers/staging-edit-attributes.mjs";
import { KEEPS_ITS_HEIGHT_CLASS, holdsPageSections, keepsItsHeight }
  from "%/dynamic-component-editor/helpers/page-section-height.mjs";
import {
  hasCompiledElement,
  readCompiledOption,
  getCompiledElement,
  mergeCompiledBundle,
} from "%/dynamic-components/render-engine/compiled-payload-store";
import { loadCompiledBundle } from "%/dynamic-components/render-engine/compiled-payload-fetch";
import { normalizeDeprecatedContainerEntry } from "%/dynamic-component-editor/helpers/deprecated-container-conversion";
import { normalizeDeprecatedElementTypeEntry } from "%/dynamic-component-editor/helpers/deprecated-element-type-aliases";

const unthreadedThemeModeReports = new Set();

function reportUnthreadedThemeMode(dynamicComponentId, elementId, themeMode) {
  const key = `${dynamicComponentId}:${elementId}`;
  if (unthreadedThemeModeReports.has(key)) {
    return;
  }
  unthreadedThemeModeReports.add(key);
  console.error( // eslint-disable-line no-console
    `[DynamicComponentProductionElement] element ${elementId} of DC ${dynamicComponentId} was rendered `
      + `without a threaded \`theme-mode\` prop (got ${JSON.stringify(themeMode)}). Its parent must pass `
      + "the mode down; falling back to the visitor's browser preference for this render.",
  );
}

export default {
  name: "DynamicComponentProductionElement",
  mixins: [
    DomStateMixinProduction,
    AnimationsMixinProduction,
    AppearanceMixinProduction,
    PositionMixinProduction,
    DynamicElementEventMixinProduction,
  ],
  inject: {

    scaledAncestorFn: {
      default: () => () => false,
    },
  },
  provide() {
    return {
      renderingInProduction: this.isProduction,
      scaledAncestorFn: () => this.hasScaledAncestor || this.scaleRequested,
    };
  },
  props: {
    dynamicComponentId: {
      type: [String, Number],
      required: true,
    },
    elementId: {
      type: [String, Number],
      required: true,
    },
    isProduction: {
      type: Boolean,
      required: false,
      default: false,
    },
    ancestorIds: {
      type: Array,
      required: false,
      default: () => [],
    },
    inPageRoot: {
      type: Boolean,
      required: false,
      default: false,
    },
    themeMode: {
      type: String,
      required: false,
      default: "",
    },
  },
  data() {
    return { compiledReady: true };
  },
  computed: {
    effectiveAnimationEffects() {
      return this.dceFrameKey !== null ? null : this.animationEffects;
    },
    stagingEditAttrs() {
      return stagingEditAttributes({
        isStagingSurface: isStagingRenderSurface(window.blclient?.data),
        isProduction: this.isProduction,
        elementId: this.elementId,
        elementType: this.elementType,
        dynamicComponentId: this.dynamicComponentId,
      });
    },
    dynamicComponent() {
      const dc = DynamicComponent.get(this.dynamicComponentId, this.isProduction);
      if (!dc) {
        throw new DynamicComponentError(
          "DynamicComponentProductionElement: DynamicComponent.get() returned null",
          { dynamicComponentId: this.dynamicComponentId, elementId: this.elementId, isProduction: this.isProduction },
        );
      }
      return dc;
    },
    entry() {
      const ready = this.compiledReady;
      if (hasCompiledElement(this.elementId)) {
        return normalizeDeprecatedContainerEntry(
          normalizeDeprecatedElementTypeEntry(getCompiledElement(this.elementId)),
        );
      }
      if (!ready) {
        return null;
      }
      const entry = this.dynamicComponent.getElement(this.elementId);
      if (!entry) {
        throw new DynamicComponentError(
          "DynamicComponentProductionElement: no entry for element",
          { dynamicComponentId: this.dynamicComponentId, elementId: this.elementId, isProduction: this.isProduction },
        );
      }
      return normalizeDeprecatedContainerEntry(normalizeDeprecatedElementTypeEntry(entry));
    },
    elementType() {
      return this.entry?.type ?? null;
    },
    holdsPageSections() {
      return holdsPageSections({ elementId: this.elementId, rootElementId: this.dynamicComponent.rootElementId, elementType: this.elementType });
    },
    keepsItsHeightClass() {
      return KEEPS_ITS_HEIGHT_CLASS;
    },
    keepsItsHeight() {
      return keepsItsHeight({ inPageRoot: this.inPageRoot, layoutHeight: this.getOptionValue(`${this.activeState}.layout-height`) });
    },
    childrenIds() {
      const ids = this.entry?.childrenIds ?? [];
      return ids.filter(
        childId => String(childId) !== String(this.elementId)
          && !this.ancestorIds.includes(String(childId)),
      );
    },
    childAncestorIds() {
      return [...this.ancestorIds, String(this.elementId)];
    },
    paintedThemeMode() {
      if (isVisitorThemeMode(this.themeMode)) {
        return this.themeMode;
      }
      reportUnthreadedThemeMode(this.dynamicComponentId, this.elementId, this.themeMode);
      return readVisitorThemeMode();
    },
    resolvedElementVueComponent() {
      const component = this.dynamicComponent.getElementVueComponent(this.elementType, true);
      if (component) {
        return component;
      }
      console.warn( // eslint-disable-line no-console
        "DynamicComponentProductionElement: no Vue component for this elementType on this page — either the type is no longer registered, or it is an entity element whose context this page does not provide (see pageContexts and the page's provides-contexts / required-contexts) — falling back to InvalidElement",
        {
          dynamicComponentId: this.dynamicComponentId,
          elementId: this.elementId,
          elementType: this.elementType,
          pageContexts: this.dynamicComponent.availableContextsEntities.map(context => context.name).join(", "),
        },
      );
      return InvalidElement;
    },
    isLayoutElement() {
      return DynamicElement.isElementOfTypeLayout(this.elementType);
    },
    isOwnRootContainingBlock() {
      const value = this.getOptionValue(`${this.activeState}.positioning`)?.["position"];
      return (!value || value === "static")
        && String(this.elementId) === String(this.dynamicComponent.rootElementId);
    },
    myPositionType() {
      const value = this.getOptionValue(`${this.activeState}.positioning`)?.["position"];
      if (value === "unset") {
        return undefined;
      }
      if (this.isOwnRootContainingBlock) {
        return "relative";
      }
      return value;
    },
    myPositionValues() {
      if (this.isOwnRootContainingBlock) {
        return {};
      }
      return PositionMixinProduction.computed.myPositionValues.call(this);
    },
  },
  async created() {
    await this.ensureEmbeddedCompiledTree();
    this.ensureChildElementsLoaded();
  },
  methods: {
    async ensureEmbeddedCompiledTree() {
      if (!isCompiledRenderSurface()) {
        return;
      }
      const rootId = this.dynamicComponent?.rootElementId;
      if (rootId === undefined || String(this.elementId) !== String(rootId) || hasCompiledElement(this.elementId)) {
        return;
      }
      const destinationId = window.blclient?.data?.themeDestinationId ?? 0;
      const dcId = this.dynamicComponentId;
      if (!destinationId || !dcId) {
        return;
      }
      const env = this.isProduction ? "destination" : "staging";
      const descriptorMap = window.blclient?.data?.[
        this.isProduction ? "productionCompiledDescriptors" : "stagingCompiledDescriptors"
      ] ?? {};
      const rootDescriptor = window.blclient?.data?.[
        this.isProduction ? "productionCompiledDescriptor" : "stagingCompiledDescriptor"
      ] ?? null;
      const version = descriptorMap[dcId]?.version
        ?? (String(rootDescriptor?.dynamicComponentId) === String(dcId) ? rootDescriptor?.version : undefined);
      this.compiledReady = false;
      try {
        const { bundle } = await loadCompiledBundle(env, { dynamicComponentId: dcId, version });
        if (bundle?.payload) {
          mergeCompiledBundle(bundle);
        }
      } finally {
        this.compiledReady = true;
      }
    },
    ensureChildElementsLoaded() {
      if (this.isProduction) {
        return;
      }
      if (hasCompiledElement(this.elementId)) {
        return;
      }
      if (!isBuilderAppBooted()) {
        return;
      }
      const app = App.get(PLATFORM_ENTITY_APP_NAME);
      const pk = { id: String(this.elementId) };
      const alreadyLoaded = app.getFeedMeta(
        "Element",
        pk,
        "childElements",
        "hasLoadedAtLeastOnce",
      );
      if (alreadyLoaded) {
        return;
      }
      app.getFeedStore("Element", pk, "childElements")?.triggerInitialLoad?.();
    },
    getOptionValue(key) {
      if (!this.elementType) {
        return null;
      }
      const [state, optionKey] = key.split(".");
      const [optionState, breakpoint] = state.split(":");
      const dc = this.dynamicComponent;

      const overrides = this.phaseOptionOverrides;
      if (overrides && Object.prototype.hasOwnProperty.call(overrides, optionKey)) {
        return DynamicOption.processElementOption(
          { type: this.elementType, options: this.entry.options },
          optionKey,
          breakpoint,
          optionState,
          overrides[optionKey],
          this.paintedThemeMode,
          { themeMode: this.paintedThemeMode, themeId: dc.themeId, theme: dc.theme, dynamicComponent: dc, breakpoints: dc.breakpoints ?? [] },
        ).c ?? "unset";
      }

      if (hasCompiledElement(this.elementId)) {
        const compiled = readCompiledOption(
          this.elementId,
          optionKey,
          this.paintedThemeMode,
          breakpoint,
          optionState,
        );
        if (compiled !== undefined) {
          return compiled;
        }
      }

      const value = DynamicOption.getCompiledOptionValue(
        { type: this.elementType, options: this.entry.options },
        optionKey,
        breakpoint,
        optionState,
        {
          themeMode: this.paintedThemeMode,
          themeId: dc.themeId,
          theme: dc.theme,
          dynamicComponent: dc,
          breakpoints: dc.breakpoints ?? [],
        },
      );
      return value ?? "unset";
    },
  },
};
</script>

<style lang="scss" scoped>
.element-keeps-its-height {
  flex-shrink: 0;
}

.element-hidden {
  display: none;
}

.element-rotate {
  rotate: v-bind(myRotate)
}

.element-scale {
  zoom: v-bind(myScale)
}

.element-blur {
  filter: v-bind(myBlur)
}

.element-opacity {
  opacity: v-bind(myOpacity)
}

.element-z-index {
  z-index: v-bind(myZIndex)
}

.element-border-radius {
  border-radius: v-bind(myBorderRadius)
}

.element-position {
  position: v-bind(myPositionType) !important;
  top: v-bind(myPositionTop) !important;
  bottom: v-bind(myPositionBottom) !important;
  left: v-bind(myPositionLeft) !important;
  right: v-bind(myPositionRight) !important;
}

</style>
